import React from 'react'
import {
    Box,
    Flex,
    Heading,
    Icon,
    Skeleton,
    Text,
    Wrap,
    WrapItem,
} from '@chakra-ui/react'
import { chakra } from '@chakra-ui/system'
import Link from 'next/link'
import { Globe } from '@emotion-icons/feather'
import { ResultOf } from '@graphql-typed-document-node/core'
import { gql } from '@urql/core'
import { Company } from 'entities/company'
import { Job } from 'entities/job'
import { Location } from 'entities/location'
import { formatRemuneration, Remuneration } from 'entities/remuneration'
import { Tag } from 'entities/tag'
import { routes } from 'routes'
import { Avatar } from 'components/images/Avatar'
import PiggyBank from 'public/img/piggy_bank.svg'

const GlobeIcon = chakra(Globe)

export const FRAGMENT_JOB_CARD_SMALL = gql<
    Pick<Job, 'id' | 'title' | 'slug' | 'remote'> & {
        company: Pick<Company, 'id' | 'name' | 'logo'>
        location: Pick<Location, 'country'>
        remuneration: Pick<
            Remuneration,
            'competitive' | 'min' | 'max' | 'currency' | 'equity'
        >
        tags: Pick<Tag, 'id' | 'label' | 'weight' | 'slug'>[]
    }
>`
    fragment JobCardSmall on Job {
        id
        title
        slug
        remote
        company {
            id
            name
            logo
        }
        location {
            country
        }
        remuneration {
            competitive
            min
            max
            currency
            timePeriod
            equity
        }
        tags {
            id
            label
            weight
            slug
        }
    }
`

export type JobCardSmallData = ResultOf<typeof FRAGMENT_JOB_CARD_SMALL>

export const JobCardSmall = ({ job }: { job?: JobCardSmallData }) => {
    const isLoaded = Boolean(job)
    const {
        title = 'Title',
        slug,
        remuneration = { currency: 'USD' },
        company,
        location = { country: 'title' },
        remote = true,
    } = job ?? {}
    const href = slug ? routes.job({ slug: slug }) : '#'

    return (
        <Link href={href} passHref>
            <Flex
                as="a"
                direction="column"
                justify="space-between"
                p="15px"
                borderWidth="1px"
                borderColor="gray.dividers"
                borderRadius="16px"
                backgroundColor="white"
            >
                <Box>
                    <Skeleton isLoaded={isLoaded}>
                        <Heading as="h3" size="sm" mb="4px" noOfLines={3}>
                            {title}
                        </Heading>
                    </Skeleton>
                </Box>

                <Flex mt="12px" direction="column" alignItems="start" gridGap="4px">
                    <Flex align="center" gridGap="8px">
                        {company?.logo && (
                            <Avatar
                                variant="company"
                                src={company.logo}
                                boxSize="24px"
                                isLoaded={isLoaded}
                            />
                        )}
                        <Skeleton isLoaded={isLoaded}>
                            <Text variant="functionalNormal">
                                {isLoaded ? company?.name : 'The company'}
                            </Text>
                        </Skeleton>
                    </Flex>
                </Flex>

                <Wrap align="center" mt="17px" spacingX="16px" spacingY="6px">
                    <WrapItem>
                        <Skeleton isLoaded={isLoaded}>
                            <Flex alignItems="center" gridGap="4px">
                                <Icon as={PiggyBank} width="19px" height="19px" />

                                <Text
                                    w="100%"
                                    fontWeight="semibold"
                                    variant="functionalSemiBold"
                                >
                                    {formatRemuneration(remuneration)}
                                </Text>
                            </Flex>
                        </Skeleton>
                    </WrapItem>

                    <WrapItem>
                        <Skeleton isLoaded={isLoaded}>
                            <Flex alignItems="center" gridGap="4px">
                                {remote && (
                                    <>
                                        <GlobeIcon boxSize="16px" strokeWidth={1.5} />
                                        <Text
                                            variant="functionalNormal"
                                            lineHeight="23px"
                                            letterSpacing="-0.01em"
                                            fontWeight="medium"
                                        >
                                            Remote
                                        </Text>
                                    </>
                                )}
                            </Flex>
                        </Skeleton>
                    </WrapItem>
                </Wrap>
            </Flex>
        </Link>
    )
}
